import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { AlertTriangle, Check } from "lucide-react";
import { SmsStatusBadge } from "./SmsStatusBadge";

const KIND = { auto: "24-hour reminder", manual: "Manual reminder", reschedule: "Reschedule text", cancellation: "Cancellation text", pause: "Pause text", walkthrough_confirm: "Walk-through confirmation" };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-AU", { timeZone: "Australia/Brisbane", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }) : "");

export default function SmsFailedList({ refreshKey, onReviewed }) {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => { api.get("/sms/failed").then((r) => setRows(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const review = async (ids = []) => {
    try {
      const { data } = await api.post("/sms/failed/review", { ids });
      toast.success(`${data.reviewed} marked reviewed`);
      load(); onReviewed?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (!rows.length) return null;
  return (
    <section className="border border-destructive/40 bg-destructive/5 p-4 sm:p-8 rounded-sm" data-testid="sms-failed-list">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 font-medium text-destructive">
          <AlertTriangle className="w-4 h-4" /> {rows.length} client text{rows.length === 1 ? "" : "s"} didn't get through
        </div>
        <Button size="sm" variant="outline" onClick={() => review()} data-testid="sms-failed-review-all" className="rounded-sm gap-1.5">
          <Check className="w-4 h-4" /> Mark all reviewed
        </Button>
      </div>
      <div className="space-y-2">
        {rows.map((r) => (
          <div key={r.id} className="flex flex-wrap items-start gap-3 rounded-sm bg-card border border-border/60 p-3" data-testid={`sms-failed-${r.id}`}>
            <div className="flex-1 min-w-[200px]">
              <div className="text-sm font-medium">{r.customer_name || r.to} <span className="text-xs text-muted-foreground font-mono">{r.to}</span></div>
              <div className="text-xs text-muted-foreground">{KIND[r.kind] || r.kind} · {fmt(r.created_at)}
                {r.job_id && <> · <Link to={`/jobs?job=${r.job_id}`} className="text-brand hover:underline">booking {fmt(r.booking_at)}</Link></>}</div>
              {r.error && <div className="text-xs text-destructive mt-1">{r.error}</div>}
            </div>
            <SmsStatusBadge status={r.status} />
            <Button size="sm" variant="ghost" onClick={() => review([r.id])} data-testid={`sms-failed-review-${r.id}`} className="rounded-sm h-7">Mark reviewed</Button>
          </div>
        ))}
      </div>
    </section>
  );
}
